import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Button } from 'antd'
import { constants } from '../../constants'
import { forceLogout } from '../../store/action/graphql.action'
import {
  PHASE,
  ACTIVITY_KEY,
  parseDurationMs,
  idleState,
  passiveActivityRenews,
} from '../../util/idletimeout'

// Signs an UNATTENDED clinician out. The policy, and the reasoning for why this
// exists alongside (not instead of) the server's 5h token expiry, is in
// util/idletimeout.js — read that before changing any of this.
//
// This file is wiring only: listen for interaction, tick a clock, show a
// warning, then hand off to the shared forceLogout().

// How often the clock is checked. Cheap — one localStorage read and some
// arithmetic — and it bounds how late a logout can be, so it also sets the
// countdown's resolution.
const TICK_MS = 1000

// Interaction is recorded to localStorage at most this often. Without it,
// mousemove would write on every pixel of travel; localStorage writes are
// synchronous and cross-tab, so that is a real cost on a long chart page.
// The throttle is far below any sane idle window, so it cannot cause a
// premature logout.
const WRITE_THROTTLE_MS = 5000

// Passive interaction. `mousemove` is included so that normal work never trips
// the timeout — and is specifically NOT trusted once the warning is up, which
// is enforced by passiveActivityRenews(). See the note there.
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
]

const readLastActivity = () => {
  try {
    return Number(window.localStorage.getItem(ACTIVITY_KEY))
  } catch (err) {
    // Private mode, blocked site data, or a storage quota error. Returning null
    // makes idleState() treat the session as starting now, which keeps a
    // clinician working rather than locking them out of a browser whose storage
    // we cannot read. The 5h server cap is unaffected either way.
    return null
  }
}

const writeLastActivity = (ts) => {
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(ts))
  } catch (err) {
    /* see readLastActivity — never let storage failure break the admin */
  }
}

export default function IdleTimeout({ enabled }) {
  const timeoutMs = parseDurationMs(constants.IDLE_TIMEOUT_MS, 30 * 60 * 1000)
  const warningMs = parseDurationMs(constants.IDLE_WARNING_MS, 2 * 60 * 1000)

  const [warning, setWarning] = useState(null) // null | msUntilLogout
  // Mirrors `warning` for the event handlers, which must not be re-bound on
  // every tick just to see the current phase.
  const phaseRef = useRef(PHASE.ACTIVE)
  const lastWriteRef = useRef(0)
  // Guards against a second forceLogout while the first is still unwinding —
  // the interval can tick again before the browser navigates.
  const loggingOutRef = useRef(false)

  const recordActivity = useCallback((force) => {
    const now = Date.now()
    if (!force && !passiveActivityRenews(phaseRef.current)) return
    if (!force && now - lastWriteRef.current < WRITE_THROTTLE_MS) return
    lastWriteRef.current = now
    writeLastActivity(now)
  }, [])

  const staySignedIn = useCallback(() => {
    phaseRef.current = PHASE.ACTIVE
    setWarning(null)
    // `force` bypasses both the phase gate (we are in WARNING, where passive
    // events are ignored) and the write throttle. Writing to localStorage is
    // also what dismisses the warning in any OTHER open tab on its next tick.
    recordActivity(true)
  }, [recordActivity])

  useEffect(() => {
    if (!enabled) {
      setWarning(null)
      phaseRef.current = PHASE.ACTIVE
      return undefined
    }

    // Start the clock at mount. A session that has just begun must not inherit
    // a stale timestamp from a previous one.
    recordActivity(true)

    const onActivity = () => recordActivity(false)
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    )

    const interval = window.setInterval(() => {
      if (loggingOutRef.current) return
      const state = idleState({
        now: Date.now(),
        lastActivity: readLastActivity(),
        timeoutMs,
        warningMs,
      })
      phaseRef.current = state.phase

      if (state.phase === PHASE.EXPIRED) {
        loggingOutRef.current = true
        setWarning(null)
        forceLogout(constants.MESSAGE.IDLE_LOGOUT)
        return
      }
      setWarning(state.phase === PHASE.WARNING ? state.msUntilLogout : null)
    }, TICK_MS)

    return () => {
      window.clearInterval(interval)
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onActivity)
      )
    }
  }, [enabled, timeoutMs, warningMs, recordActivity])

  if (!enabled || warning === null) return null

  const seconds = Math.max(0, Math.ceil(warning / 1000))

  return (
    // antd 4.x takes `visible`, not v5's `open`. This app is on antd ^4.1.3 and
    // uses `visible` in 40 other places; `open` here would silently render
    // nothing, so the timeout would fire with no warning ever shown.
    <Modal
      visible={true}
      // No close affordance anywhere: dismissing this without a decision would
      // leave the clinician believing the session was extended when it was not.
      closable={false}
      maskClosable={false}
      keyboard={false}
      title='Still there?'
      footer={[
        <Button key='logout' onClick={() => {
          loggingOutRef.current = true
          forceLogout()
        }}>
          Log out now
        </Button>,
        <Button key='stay' type='primary' onClick={staySignedIn}>
          Stay signed in
        </Button>,
      ]}
    >
      <p>
        You will be signed out in <strong>{seconds}</strong> second
        {seconds === 1 ? '' : 's'} because the admin has been idle.
      </p>
      <p style={{ marginBottom: 0, opacity: 0.7 }}>
        This protects patient information left on an unattended screen.
      </p>
    </Modal>
  )
}
